/**
 * Landscape-only gameplay on phones and tablets. Janeks, 2026-10-19:
 * *"Implement mobile gameplay only on horizontal layout in game"*.
 *
 * While the game is mounted (settlement, locations, the arena, the practice
 * yard — everything after sign-in), a touch device held upright gets a
 * full-screen "turn it sideways" card over the game, which also stops taps from
 * reaching the world underneath. The landing page and sign-in stay usable in
 * portrait; they are not gameplay.
 *
 * Decided by CSS alone (`pointer: coarse` + `orientation: portrait`), so it
 * follows the device the instant it rotates, with no resize listener to get out
 * of step. A desktop window that happens to be tall is not a phone and is left
 * alone. Where the browser allows it (installed or full-screen), the screen is
 * also asked to lock to landscape; most mobile browsers refuse outside
 * full-screen, which is fine — the card is the real gate.
 */

const STYLE_ID = 'ah-landscape-gate-style';

const CSS = `
.ah-rotate { display: none; }
@media (pointer: coarse) and (orientation: portrait) {
  .ah-rotate {
    position: fixed; inset: 0; z-index: 2147483000; display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 18px;
    padding: max(24px, env(safe-area-inset-top)) max(24px, env(safe-area-inset-right))
      max(24px, env(safe-area-inset-bottom)) max(24px, env(safe-area-inset-left));
    background: radial-gradient(ellipse at center, #1c150d 0%, #0b0906 75%);
    color: #e8dcc4; text-align: center; touch-action: none;
  }
}
.ah-rotate-phone {
  width: 54px; height: 92px; border: 3px solid #d9a441; border-radius: 10px; position: relative;
  animation: ah-rotate-turn 2.4s ease-in-out infinite;
}
.ah-rotate-phone::after {
  content: ""; position: absolute; left: 50%; bottom: 6px; width: 14px; height: 3px; margin-left: -7px;
  border-radius: 2px; background: #d9a441;
}
.ah-rotate-title { margin: 0; font: 700 1.2rem/1.2 "Oswald", system-ui, sans-serif; letter-spacing: 0.1em; text-transform: uppercase; color: #ffbd5c; }
.ah-rotate-text { margin: 0; max-width: 28ch; font: 0.95rem/1.4 system-ui, sans-serif; color: #c9bba0; }
@keyframes ah-rotate-turn {
  0%, 20% { transform: rotate(0deg); }
  50%, 80% { transform: rotate(-90deg); }
  100% { transform: rotate(0deg); }
}
@media (prefers-reduced-motion: reduce) { .ah-rotate-phone { animation: none; transform: rotate(-90deg); } }
`;

/** Mounts the gate; returns its teardown. */
export function mountLandscapeGate(doc: Document = document): () => void {
  if (!doc.getElementById(STYLE_ID)) {
    const style = doc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    doc.head.appendChild(style);
  }

  const gate = doc.createElement('div');
  gate.className = 'ah-rotate';
  gate.setAttribute('role', 'alertdialog');
  gate.setAttribute('aria-label', 'Turn your device sideways');
  gate.innerHTML = '<div class="ah-rotate-phone" aria-hidden="true"></div>'
    + '<p class="ah-rotate-title">Turn your device sideways</p>'
    + '<p class="ah-rotate-text">After Humanity plays in landscape. Rotate your phone to keep going.</p>';
  doc.body.appendChild(gate);

  // Best effort: only honoured in full-screen or an installed app.
  const orientation = (globalThis.screen as (Screen & { orientation?: { lock?: (o: string) => Promise<void> } }) | undefined)?.orientation;
  orientation?.lock?.('landscape').catch(() => { /* refused outside full-screen; the card covers it */ });

  return () => {
    gate.remove();
  };
}
